import { useEffect, useState } from "react";
import { config, BRAIN_SUPPLY_CAP } from "../config";
import {
  fetchAllBrains,
  fetchBrainValidators,
  fetchNetworkStats,
  type BrainSummary,
  type NetworkStats,
  type ValidatorRow,
} from "../lib/explore";
import { bps, fmtToken, shortAddr } from "../lib/format";
import { Badge, Card, Notice, Stat } from "../components/ui";

/** Hand a (brain, validator) pair to the Stake page across the hash navigation. */
export function stashStakePrefill(brainId: number, validator?: string) {
  sessionStorage.setItem("basedai.stake.prefill", JSON.stringify({ brainId, validator }));
}

/** Example data shown ONLY when the network can't be loaded (preview mode / RPC down), so the
 *  explorer demonstrates itself pre-deployment. Every surface it touches is badged "Example". */
const T = 10n ** 18n;
const SAMPLE_BRAINS: BrainSummary[] = [
  { id: 7, owner: "0xBA5EBA5EBA5EBA5EBA5EBA5EBA5EBA5EBA5E0007", modelURI: "hf.co/basedai/pepe-70b-instruct", active: true, registrationFee: 100n * T, ownerSplitBps: 800, minerShareBps: 7609, miners: 4, validators: 2, stake: 12_500n * T },
  { id: 8, owner: "0xBA5EBA5EBA5EBA5EBA5EBA5EBA5EBA5EBA5E0008", modelURI: "ipfs://bafy.../mixtral-8x7b", active: true, registrationFee: 100n * T, ownerSplitBps: 500, minerShareBps: 8000, miners: 1, validators: 1, stake: 3_200n * T },
  { id: 9, owner: "0xBA5EBA5EBA5EBA5EBA5EBA5EBA5EBA5EBA5E0009", modelURI: "", active: false, registrationFee: 100n * T, ownerSplitBps: 800, minerShareBps: 7609, miners: 0, validators: 0, stake: 0n },
];
const SAMPLE_VALIDATORS: Record<number, ValidatorRow[]> = {
  7: [
    { address: "0x1111111111111111111111111111111111111111", stake: 9_000n * T, feesEarned: 214n * T },
    { address: "0x2222222222222222222222222222222222222222", stake: 3_500n * T, feesEarned: 86n * T },
  ],
  8: [{ address: "0x3333333333333333333333333333333333333333", stake: 3_200n * T, feesEarned: 12n * T }],
  9: [],
};
const SAMPLE_STATS: NetworkStats = {
  activeBrains: 2,
  totalMiners: 5,
  totalValidators: 3,
  totalStaked: 15_700n * T,
  pricePerByte: 0n,
  pricePerRequest: 0n,
};

function StatsRow({ stats }: { stats: NetworkStats | null }) {
  return (
    <div className="grid cols-3">
      <Card><Stat label="Active Brains" value={stats ? `${stats.activeBrains} / ${BRAIN_SUPPLY_CAP}` : "—"} /></Card>
      <Card><Stat label="Miners / Validators" value={stats ? `${stats.totalMiners} / ${stats.totalValidators}` : "—"} /></Card>
      <Card><Stat label="Total staked" value={stats ? fmtToken(stats.totalStaked) : "—"} unit="BASED" /></Card>
    </div>
  );
}

function BrainGrid({ brains, sample }: { brains: BrainSummary[]; sample: boolean }) {
  const shown = brains.filter((b) => b.active || b.id >= 7);
  return (
    <div className="grid cols-2" style={{ marginTop: 18 }}>
      {shown.map((b) => (
        <a key={b.id} href={`#/brains/${b.id}`} style={{ textDecoration: "none", color: "inherit" }}>
          <Card
            title={`Brain #${b.id}`}
            sub={b.modelURI || "no model set"}
            right={
              <div className="row">
                {sample && <Badge kind="mystic">Example</Badge>}
                {b.active ? <Badge kind="ok">Active</Badge> : <Badge kind="warn">Inactive</Badge>}
              </div>
            }
          >
            <div className="grid cols-3">
              <Stat label="Miners" value={b.miners} />
              <Stat label="Validators" value={b.validators} />
              <Stat label="Staked" value={fmtToken(b.stake)} unit="BASED" />
            </div>
          </Card>
        </a>
      ))}
    </div>
  );
}

function BrainDetail({ brain, sample }: { brain: BrainSummary; sample: boolean }) {
  const [validators, setValidators] = useState<ValidatorRow[] | null>(null);

  useEffect(() => {
    let ok = true;
    setValidators(null);
    if (sample) {
      setValidators(SAMPLE_VALIDATORS[brain.id] ?? []);
      return;
    }
    fetchBrainValidators(brain.id).then((rows) => {
      if (ok) setValidators(rows);
    });
    return () => {
      ok = false;
    };
  }, [brain.id, sample]);

  const totalStake = validators?.reduce((s, v) => s + v.stake, 0n) ?? 0n;

  return (
    <>
      <div className="row" style={{ marginTop: 18 }}>
        <a href="#/brains">← All Brains</a>
      </div>
      <div className="grid cols-2" style={{ marginTop: 12 }}>
        <Card
          title={`Brain #${brain.id}`}
          sub={brain.modelURI || "no model set"}
          right={
            <div className="row">
              {sample && <Badge kind="mystic">Example</Badge>}
              {brain.active ? <Badge kind="ok">Active</Badge> : <Badge kind="warn">Inactive</Badge>}
            </div>
          }
        >
          <div className="stack">
            <Stat label="Owner" value={<span className="mono" style={{ fontSize: 13 }}>{shortAddr(brain.owner)}</span>} />
            <div className="grid cols-2">
              <Stat label="Owner split" value={bps(brain.ownerSplitBps)} />
              <Stat label="Miner share (of node)" value={bps(brain.minerShareBps)} />
            </div>
            <div className="grid cols-2">
              <Stat label="Registration fee" value={fmtToken(brain.registrationFee)} unit="BASED" />
              <Stat label="Total staked" value={fmtToken(brain.stake)} unit="BASED" />
            </div>
            <div className="row">
              <a className="btn sm" href="#/inference">Run inference →</a>
              <a className="btn ghost sm" href="#/operate">Operate / join →</a>
            </div>
          </div>
        </Card>

        <Card title="Validator leaderboard" sub="Delegate to a validator to share its fee income.">
          {validators === null ? (
            <div className="muted">Loading…</div>
          ) : validators.length === 0 ? (
            <Notice>
              No active validators on this Brain yet — the validator fee share currently folds to miners.
              Be the first: register on the Operate page.
            </Notice>
          ) : (
            <table className="doc-table">
              <thead>
                <tr>
                  <th>Validator</th>
                  <th>Stake</th>
                  <th>Share</th>
                  <th>Fees earned</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {validators.map((v) => (
                  <tr key={v.address}>
                    <td>
                      <a className="mono" href={`${config.l2.explorer}/address/${v.address}`} target="_blank" rel="noreferrer">
                        {shortAddr(v.address)}
                      </a>
                    </td>
                    <td>{fmtToken(v.stake)}</td>
                    <td>{totalStake > 0n ? `${Number((v.stake * 1000n) / totalStake) / 10}%` : "—"}</td>
                    <td>{fmtToken(v.feesEarned)}</td>
                    <td>
                      <a
                        className="btn ghost sm"
                        href="#/stake"
                        onClick={() => stashStakePrefill(brain.id, v.address)}
                      >
                        Stake
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
}

export function Brains({ route }: { route: string }) {
  const [brains, setBrains] = useState<BrainSummary[] | null>(null);
  const [stats, setStats] = useState<NetworkStats | null>(null);
  const [sample, setSample] = useState(false);

  useEffect(() => {
    let ok = true;
    fetchAllBrains().then(async (all) => {
      if (!ok) return;
      if (all.length === 0) {
        // Nothing loadable (preview mode / RPC down): demonstrate the page with example data.
        setSample(true);
        setBrains(SAMPLE_BRAINS);
        setStats(SAMPLE_STATS);
        return;
      }
      setBrains(all);
      setStats(await fetchNetworkStats(all));
    });
    return () => {
      ok = false;
    };
  }, []);

  const sub = route.startsWith("/brains/") ? Number(route.slice("/brains/".length)) : null;
  const selected =
    sub !== null && Number.isInteger(sub) && brains ? brains.find((b) => b.id === sub) ?? null : null;

  return (
    <>
      <div className="section-title">Brain explorer</div>
      {sample && (
        <div style={{ marginBottom: 14 }}>
          <Notice kind="warn">
            <strong>Example data.</strong> The network is not configured (preview mode) or the RPC is
            unreachable, so everything below is a sample to show how the explorer looks — none of it is
            on-chain.
          </Notice>
        </div>
      )}
      <StatsRow stats={stats} />
      {brains === null ? (
        <div className="muted" style={{ marginTop: 18 }}>Loading Brains…</div>
      ) : selected ? (
        <BrainDetail brain={selected} sample={sample} />
      ) : (
        <BrainGrid brains={brains} sample={sample} />
      )}
    </>
  );
}
